import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { compose, head, prop, omit, over, lensProp, assoc } from 'ramda';
import { toast } from 'react-toastify';

import { Formik, Form } from 'formik';
import { Grid, Checkbox, Typography, TextField, Chip, Tooltip, FormControlLabel } from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';

import Modal from '../../../components/Modal';
import Card from '../../../components/Card';
import CardFooter from '../../../components/CardFooter';
import Input from '../../../components/Input';
import Loader from '../../../components/Loader';
import Button from '../../../components/Button';
import Select from '../../../components/Select';
import Status from '../../../components/Status';
import PasswordUpdate from '../../../components/PasswordUpdate';
import EmployeesDescription from './EmployeesDescription';

import { getPasswordStrength, getPasswordRules } from '../../../components/PasswordUpdate/resources';

import { setEmptyEmployee } from '../store/actions';
import { loadEmployee, upsertEmployee, updateMainUser, updateEmployeePassword, setMainUserSL, loadRestrictions } from '../store/thunk';
import { loadFilters } from 'components/Filter/store/thunk';
import { setEstablishmentId, setPreviousEstablishmentId } from 'pages/BusinessInfo/store/actions';

import { formatFilters, formatAvailableEstablishments } from '../../../helpers/formatters';
import { RESTRICTION_PAGE } from '../../../helpers/restrictions';
import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { validations, mainUserValidations } from './validations';

import { useStyles } from './styles';

export default function EmployeesRegistration({ formikProps }) {
  const dispatch = useDispatch();

  const classes = useStyles();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const history = useHistory();

  const pathSegments = history.location.pathname.split('/');
  const isEmployeeRegister = history.location.pathname == '/novo-usuario';
  const isEmployeeEdit = history.location.pathname == '/editar-usuario';
  const isInitialRegistration = pathSegments.includes('cadastro-inicial') && pathSegments.includes('cadastro-de-usuario');

  const [isPasswordUpdateOpen, setIsPasswordUpdateOpen] = useState(false);
  const [isPasswordUpdateLoading, setIsPasswordUpdateLoading] = useState(false);

  const { userId, userRestrictions } = useSelector(state => state.profile);
  const { establishmentId, establishmentUuid } = useSelector(state => state.businessInfo);
  const { isAdminUser } = useSelector(state => state.profile.submit);
  const { mainUser } = useSelector(state => state.employeesRegistration);
  const { selects } = useSelector(state => state.filters);
  const establishments = useSelector(state => state.login.establishments);
  const situationEstablishments = useSelector(state => state.login.situation.establishments);

  const {
    employeeId,
    submit,
    isLoading,
    isMainUserLoading,
    isRestrictionsLoading,
    restrictionsApp,
    restrictionsUser
  } = useSelector(state => state.employeesRegistration);

  const siteRestrictionsType = restrictionsUser?.map(restriction => restriction?.type);
  const siteRestrictionsId = [...new Set(siteRestrictionsType)].sort();

  const appRestrictionsType = restrictionsApp?.map(restriction => restriction?.type);
  const appRestrictionsId = [...new Set(appRestrictionsType)].sort();

  const [selectedUserRestrictionsId, setSelectedUserRestrictionsId] = useState([]);
  const [selectedAppRestrictionsId, setSelectedAppRestrictionsId] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(0);
  const [isProfileVerificationModalOpen, setIsProfileVerificationModalOpen] = useState(false);
  const [formikEmployeeValues, setFormikEmployeeValues] = useState({});
  const [formikEmployeeActions, setFormikEmployeeActions] = useState({});
  const [isMainUserModalOpen, setIsMainUserModalOpen] = useState(false);

  const profilesToDisplay = [2, 3, 5];
  const isUserAdmin = +isAdminUser == 1;

  const allAvailableEstablishments = submit?.availableEstablishments;

  const availableEstablishments = formatAvailableEstablishments([...establishments, ...allAvailableEstablishments])
    ?.filter((establishment, index, self) => index == self.findIndex(establishmentIndex => establishmentIndex?.establishmentId == establishment?.establishmentId));

  const formattedAvailableEstablishments = availableEstablishments.map(establishment => {
    return {
      ...establishment,
      label: isUserAdmin ? `${establishment.establishmentId} - ${establishment.label}` : establishment.label
    }
  });

  useEffect(() => {
    if(history.location.pathname == '/novo-usuario') {
      dispatch(setPreviousEstablishmentId(establishmentId));
      dispatch(setEmptyEmployee());
      dispatch(loadRestrictions(userId));
    }
  }, [history.location.pathname]);

  useEffect(() => {
    if(isInitialRegistration) {
      formikProps.setFieldValue('employeeId', employeeId);
      formikProps.setFieldValue('establishments', [establishmentUuid]);
      dispatch(loadRestrictions(userId));
    }
  }, [isInitialRegistration, employeeId, establishmentUuid]);

  useEffect(() => {
    setSelectedUserRestrictionsId(submit?.userRestrictions);
    setSelectedAppRestrictionsId(submit?.appRestrictions);
  }, [submit]);

  useEffect(() => {
    if((history.location.pathname == '/novo-usuario') && (+selectedProfileId == 3)) {
      const restrictions = restrictionsApp
        ?.filter(restriction => +restriction?.checked == 1)
        ?.map(restriction => restriction?.id);

      setSelectedAppRestrictionsId(selectedAppRestrictionsId?.concat(restrictions));
    }
  }, [restrictionsApp, selectedProfileId]);

  const selectRestriction = (restrictions, value) => {
    return restrictions?.includes(+value)
      ? restrictions.filter(id => +id != +value)
      : restrictions.concat(+value);
  }

  const getProfiles = () => {
    let profiles = prop('options', head(formatFilters(['profile'], selects)));

    const hasStoreEstablishment = establishments
      ?.map(establishment => establishment?.establishmentTypes)
      ?.filter(establishmentType => Array.isArray(establishmentType))
      ?.some(establishmentType => establishmentType?.includes(3) || establishmentType?.includes('3'));

    if(hasStoreEstablishment) {
      profiles.concat({
        name: 'Vendedor',
        value: 7
      });
    }

    return profiles
      ?.filter(profile => +profile?.value != 9)
      ?.map(option => {
        return option?.value == ''
          ? {
            label: '--',
            value: ''
          } : {
            label: option?.name,
            value: option?.value
          }
      });
  }

  const profiles = getProfiles();

  const isCarWashWarningMessage =
    +formikEmployeeValues.profileId == 4 &&
    formattedAvailableEstablishments
      ?.filter(establishment => formikEmployeeValues?.establishments?.includes(establishment?.establishmentId))
      ?.some(establishment => establishment?.establishmentTypes?.includes(2) || establishment?.establishmentTypes?.includes("2"));

  const isCarWashWarningEstablishment = formattedAvailableEstablishments
    ?.filter(establishment => formikEmployeeValues?.establishments?.includes(establishment?.establishmentId))
    ?.filter(establishment => establishment?.establishmentTypes?.includes(2) || establishment?.establishmentTypes?.includes("2"))
    ?.map(establishment => establishment?.establishmentName)
    ?.join(', ');

  const restrictionsTypes = Array.isArray(prop('options', head(formatFilters(['restrictionType'], selects))))
    ? prop('options', head(formatFilters(['restrictionType'], selects)))?.filter(restriction => restriction.label != '--')
    : [];

  const handleInitialEstablishments = e =>
    availableEstablishments.length === 1 ? [availableEstablishments[0].establishmentId] : e;

  const initialState = compose(
    over(lensProp('establishments'), handleInitialEstablishments),
    assoc('employeeId', employeeId)
  )(submit);

  useEffect(() => {
    dispatch(loadFilters(userId, establishmentId));
  }, [establishmentId]);

  useEffect(() => {
    if(userRestrictions?.includes(RESTRICTION_PAGE.EDIT_USER)) {
      toast.error('Ação não autorizada.');
      history.push('/visao-geral');
    }

    if(userId && employeeId) {
      dispatch(loadEmployee(userId, employeeId));
    }

    if(history.location.pathname == '/novo-usuario') {
      dispatch(loadRestrictions(userId));
    }

    return () => dispatch(setEmptyEmployee());
  }, []);

  const getProfileTitle = profileId => {
    return profiles?.find(profile => profile.value == profileId)?.label;
  }

  const getEstablishmentColor = establishmentId => {
    const { appPermission, debitWarning } = situationEstablishments?.find(establishment => +establishment?.establishmentId == +establishmentId) || {};

    if(isUserAdmin) {
      if(+appPermission == 0) {
        return 'rgba(247, 145, 145, 0.5)';
      }

      if(+debitWarning == 1) {
        return 'rgba(250, 250, 67, 0.5)';
      }
    }

    return '#E0E0E0';
  }

  const convertEstablishmentIdToUuid = establishments => {
    const establishmentsUuid = availableEstablishments
      ?.map(establishment => {
        if(establishments?.includes(establishment?.establishmentId)) {
          return establishment?.uuid;
        }
      })
      ?.filter(uuid => !!uuid);

    return establishmentsUuid;
  }

  const setRestrictions = (appRestrictions, userRestrictions, profileId) => {
    switch(profileId) {
      case 2:
        return [[], userRestrictions || []];
      case 3:
        return [appRestrictions || [], []];
      case 5:
        return [appRestrictions || [], []];
      default:
        return [[], []];
    }
  }

  const filterOptions = (options, state) => {
    const OPTIONS_LIMIT = 500;
    const defaultFilterOptions = createFilterOptions();

    return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
  }

  const getRestrictionsApp = (establishments, profileId) => {
    const isAllEstablishmentsCarWash = availableEstablishments
      ?.filter(establishment => establishments?.includes(establishment?.establishmentId))
      ?.every(establishment => establishment?.establishmentTypes?.length == 1 && (establishment?.establishmentTypes?.includes(2) || establishment?.establishmentTypes?.includes('2')));

    if(isAllEstablishmentsCarWash && +profileId == 3) {
      return restrictionsApp?.filter(appRestriction => ![20, 21].includes(+appRestriction?.id));
    }

    return restrictionsApp;
  }

  const getEmployeeSiteTooltip = id => {
    if(+id == 102) {
      return 'A Aba de negócio vai sumir do Menu do topo do site.';
    }

    if(+id == 103) {
      return 'A Aba de Clientes vai sumir do Menu do topo do site.';
    }

    if(+id == 104) {
      return 'Não poderá visualizar as Notas fiscais emitidas nem configurações.';
    }

    if(+id == 111) {
      return 'Não visualiza "engrenagem" onde tem todas as configurações.';
    }

    if(+id == 113) {
      return 'Não tem acesso aos boletos e notas fiscais referente aos pagamentos da Jump.';
    }

    if(+id == 114) {
      return 'Não visualiza acesso aos comissionamentos.';
    }

    if(+id == 115) {
      return 'Não visualiza acesso as folhas de pagamento.';
    }

    if(+id == 116) {
      return 'Não visualiza o acesso a Registro de sessões.';
    }

    if(+id == 117) {
      return 'Não visualiza a Aba de Financeiro.';
    }

    if(+id == 118) {
      return 'Não visualiza a aba de Clientes.';
    }

    if(+id == 105) {
      return 'Não permite qualquer edição do cliente.';
    }

    if(+id == 106) {
      return 'Não permite qualquer cadastro do cliente.';
    }

    if(+id == 18) {
      return 'Não permite cadastrar novo estabelecimento.';
    }

    if(+id == 24) {
      return 'Não permite cadastrar Usuário.';
    }

    if(+id == 19) {
      return 'Não permite editar os dados do estabelecimento como CNPJ, endereço etc.';
    }

    if(+id == 25) {
      return 'Não permite editar qualquer dado do usuário.';
    }

    if(+id == 23) {
      return 'Não permite acessar a lista de usuários.';
    }

    if(+id == 90) {
      return 'Não permite visualizar a página Estabelecimento.';
    }

    if(+id == 107) {
      return 'Não permite remover as Ordens de Serviço.';
    }

    if(+id == 108) {
      return 'Não permite nenhuma edição nas Ordens de serviço.';
    }

    if(+id == 109) {
      return 'Não permite remoção de nenhuma transação avulsa.';
    }

    if(+id == 110) {
      return 'Não permite editar o valor da sangria.';
    }

    if(+id == 112) {
      return 'Não permite remover os comprovantes anexados.';
    }

    return '';
  }

  const getEmployeeAppTooltip = id => {
    if(+id == 1) {
      return 'Não consegue acessar a tela com as informações do cliente.';
    }

    if(+id == 2) {
      return 'Não permite editar o valor da fatura do mensalista.';
    }

    if(+id == 3) {
      return 'Não permite editar os dados dos clientes.';
    }

    if(+id == 9) {
      return 'Não permite cadastrar cliente na tela onde visualiza a lista de clientes.';
    }

    if(+id == 10) {
      return 'Não permite receber pagamentos dos mensalistas e pós-pago.';
    }

    if(+id == 22) {
      return 'Não permite acessar a tela de configurações operacionais no aplicativo.';
    }

    if(+id == 5) {
      return 'Não permite alterar dados como placa, modelo e cor do veículo após dar entrada.';
    }

    if(+id == 6) {
      return 'Não permite alterar os valores dos serviços na tela de entrada.';
    }

    if(+id == 7) {
      return 'Não permite ativar pagamento antecipado na tela de configuração e na tela de entrada do veículo.';
    }

    if(+id == 11) {
      return 'Não permite corrigir ou alterar o meio de pagamento após receber o pagamento.';
    }

    if(+id == 17) {
      return 'Não permite alterar os valores do serviço na tela de saída do veículo.';
    }

    if(+id == 19) {
      return 'Não permite cancelar uma Ordem de serviço.';
    }

    if(+id == 20) {
      return 'Não permite alterar o valor do estacionamento na tela de pagamento.';
    }

    if(+id == 21) {
      return 'Não permite alterar a tabela de estacionamento na tela de saída. O usuário ainda pode ativar o convênio.';
    }

    if(+id == 23) {
      return 'Não permite dar desconto na Ordem de serviço.';
    }

    if(+id == 24) {
      return 'Não permite alterar horário de entrada do veículo.';
    }

    if(+id == 25) {
      return 'Não permite vincular ou desvincular a placa em um cliente mensalista.';
    }

    if(+id == 4) {
      return 'Não mostra as informações financeiras do caixa na tela principal do aplicativo.';
    }

    if(+id == 8) {
      return 'Não permite que o usuário desative a cobrança de estacionamento ao selecionar um serviço.';
    }

    if(+id == 12) {
      return 'Não permite visualizar os veículos pagos na listagem de veículo.';
    }

    if(+id == 13) {
      return 'Não mostra o saldo em dinheiro na tela de fechamento e não imprime o relatório do caixa após o fechamento.';
    }

    if(+id == 14) {
      return 'Não permite imprimir o relatório de veículo no pátio.';
    }

    if(+id == 15) {
      return 'Não permite imprimir o relatório de veículo fora do pátio.';
    }

    if(+id == 16) {
      return 'Não permite reimprimir o ticket de entrada do veículo.';
    }

    if(+id == 18) {
      return 'Não mostra os valores de cada serviço na impressão da ordem de serviço.';
    }

    return '';
  }

  const handleMainUser = values => {
    dispatch(updateMainUser(userId, employeeId, values)).then(() => {
      setIsMainUserModalOpen(false);
    });
  }

  const handleRegistrationByProfile = (values, actions) => {
    const params = omit(['employeeId'], values);
    const { profileId } = params;

    if(+profileId == 4 || +profileId == 6) {
      setFormikEmployeeValues(values);
      setFormikEmployeeActions(actions);
      return setIsProfileVerificationModalOpen(true);
    }

    return handleEmployeeRegistration(values, actions);
  }

  const handleEmployeeRegistration = (values, actions) => {
    const { resetForm } = actions;
    const params = omit(['employeeId'], values);
    const { profileId, email } = params;

    params.email = email.replace(/ /g, '');

    const [appRestrictions, userRestrictions] = setRestrictions(selectedAppRestrictionsId, selectedUserRestrictionsId, profileId);

    params.appRestrictions = appRestrictions.filter(restriction => parseInt(restriction));
    params.userRestrictions = userRestrictions.filter(restriction => parseInt(restriction));
    params.establishments = convertEstablishmentIdToUuid(params.establishments);

    dispatch(upsertEmployee(userId, employeeId, params)).then(() => {
      resetForm();
      history.push('/usuarios');
    });
  }

  return(
    <Card title="Informações gerais">
      {isPasswordUpdateOpen && (
        <PasswordUpdate
          isEmployeeEdit
          isLoading={isPasswordUpdateLoading}
          isOpen={isPasswordUpdateOpen}
          setIsOpen={setIsPasswordUpdateOpen}
          onSuccess={values => {
            setIsPasswordUpdateLoading(true);

            dispatch(updateEmployeePassword(userId, employeeId, values))
              .then(() => setIsPasswordUpdateOpen(false))
              .finally(() => setIsPasswordUpdateLoading(false));
          }}
        />
      )}
      {isMainUserModalOpen && (
        <Modal
          id="mainUserModal"
          title={(+mainUser == 1) ? 'Desativar usuário principal' : 'Ativar usuário principal'}
          scroll="body"
          maxWidth="md"
          open={isMainUserModalOpen}
          onClose={() => setIsMainUserModalOpen(false)}
        >
          <Grid container>
            <Grid xs={12}>
              <Typography color='primary'>
                Deseja ativar/desativar o usuário como usuário principal (mainUser)?
              </Typography>
            </Grid>
            <Grid xs={12}>
              <Formik
                enableReinitialize
                initialValues={{ reason: '', password: '' }}
                validationSchema={mainUserValidations}
                onSubmit={handleMainUser}
              >
                {({ ...formikProps }) => {
                  return(
                    <Form>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <Input
                            name="reason"
                            label="Motivo"
                            value={formikProps.values.reason}
                            error={formikProps.errors.reason}
                            helperText={formikProps.errors.reason}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Input
                            name="password"
                            label="Senha"
                            type="password"
                            value={formikProps.values.password}
                            error={formikProps.errors.password}
                            helperText={formikProps.errors.password}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid container xs={12} justify='flex-end'>
                          <Button
                            type="submit"
                            color="primary"
                            loading={isMainUserLoading}
                          >
                            Salvar
                          </Button>
                        </Grid>
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
            </Grid>
          </Grid>
        </Modal>
      )}
      {isProfileVerificationModalOpen && (
        <Modal
          id="profileVerificationModal"
          title="Atenção"
          scroll="body"
          maxWidth="md"
          open={isProfileVerificationModalOpen}
          onClose={() => setIsProfileVerificationModalOpen(false)}
          modalActions={
            <Grid container spacing={2}>
              <Grid item>
                <Button
                  color="error"
                  onClick={() => setIsProfileVerificationModalOpen(false)}
                >
                  Cancelar
                </Button>
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  onClick={() => handleEmployeeRegistration(formikEmployeeValues, formikEmployeeActions)}
                >
                  Continuar
                </Button>
              </Grid>
            </Grid>
          }
        >
          <Typography>
            Este usuário não pode fazer recebimentos pelo aplicativo operacional.
            Cadastre com o perfil Operador ou Supervisor caso deseje que este usuário possa fazer essa função.
          </Typography>
          {isCarWashWarningMessage && (
            <>
              <br /><br />
              O estabelecimento <b>{isCarWashWarningEstablishment}</b> é uma estética automotiva, têm certeza que deseja salvar como Manobrista?
            </>
          )}
        </Modal>
      )}
      <Formik
        enableReinitialize
        initialValues={{ ...initialState, mainUser }}
        validationSchema={validations}
        onSubmit={handleRegistrationByProfile}
      >
        {({ ...formikNewProps }) => {
          const {
            errors,
            values: {
              name,
              status,
              email,
              phone,
              profileId,
              establishments,
              password,
              passwordConfirm
            },
            handleChange,
            setFieldValue
          } = formikNewProps;

          const establishmentsSelect = establishments
            ?.filter(establishment => formikNewProps?.values?.establishments?.includes(establishment?.establishmentId))

          const isEstablishmentsStoreOnly = establishmentsSelect?.length > 0
            ? establishmentsSelect?.every(establishment => establishment?.establishmentTypes?.length == 1 && +establishment?.establishmentTypes[0] == 3)
            : false;

          const isPhoneError =
            !errors.phone &&
            !formikNewProps.errors.phone &&
            (phone?.includes('(55)') || phone?.includes('(55) 1'));

          return(
            <Grid item xs={12}>
              {(+mainUser == 1 && isUserAdmin) && (
                <Grid className={classes.infoCard}>
                  <Typography className={classes.infoCardText}>
                    Este é um usuário principal.
                  </Typography>
                </Grid>
              )}
              <Form>
                <Loader isLoading={isLoading}>
                  <Grid container>
                    <Grid item md={6} xs={12} className={classes.firstGrid}>
                      <Input
                        autoFocus={!isSm}
                        name="name"
                        label="Nome"
                        value={name || formikProps?.name}
                        error={errors?.name || formikProps?.errors?.name}
                        helperText={errors?.name || formikProps?.errors?.name}
                        onChange={e => {
                          handleChange(e);

                          if(formikProps) {
                            formikProps.handleChange(e);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12} className={classes.firstGrid}>
                      <Status
                        disabled={mainUser == 1}
                        value={status || formikProps?.values?.status}
                        onChange={e => {
                          handleChange(e);

                          if(formikProps) {
                            formikProps.handleChange(e);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={6} xs={12} className={classes.grid}>
                      <Input
                        name="email"
                        label="Email"
                        placeholder="usuario@exemplo.com"
                        value={email || formikProps?.values?.email}
                        error={errors?.email || formikProps?.errors?.email}
                        helperText={errors?.email || formikProps?.errors?.email}
                        onChange={e => {
                          handleChange(e);

                          if(formikProps) {
                            formikProps.handleChange(e);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12} className={classes.grid}>
                      <Input
                        name="phone"
                        type="phone"
                        label="Telefone"
                        placeholder="(00) 0000-0000"
                        value={phone || formikProps?.values?.phone}
                        error={errors?.phone || formikProps?.errors?.phone}
                        helperText={errors?.phone || formikProps?.errors?.phone}
                        onChange={e => {
                          handleChange(e);

                          if(formikProps) {
                            formikProps.handleChange(e);
                          }
                        }}
                      />
                      {isPhoneError && (
                        <div style={{ margin: '0 14px 0 14px', color: '#ebc707' }}>
                          <small>Por favor, escreva seu telefone no formato DDD + número de celular</small>
                        </div>
                      )}
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item md={6} xs={12} className={classes.grid}>
                      <Select
                        name="profileId"
                        label="Perfil"
                        disabled={+mainUser == 1}
                        options={isEstablishmentsStoreOnly
                          ? profiles?.filter(profile => ['', 2, 7].includes(profile?.value))
                          : profiles
                        }
                        value={parseInt(profileId || formikProps?.values?.profileId, 10)}
                        error={errors?.profileId || formikProps?.errors?.profileId}
                        helperText={errors?.profileId || formikProps?.errors?.profileId}
                        onChange={e => {
                          if(isEmployeeRegister) {
                            setSelectedUserRestrictionsId([]);
                            setSelectedAppRestrictionsId([]);
                          }

                          setSelectedProfileId(e.target.value);

                          handleChange(e);

                          if(formikProps) {
                            formikProps.handleChange(e);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item md={6} xs={12} className={classes.grid}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disabled={isInitialRegistration}
                        id="establishments"
                        options={formattedAvailableEstablishments}
                        filterOptions={filterOptions}
                        value={formattedAvailableEstablishments?.filter(establishment => establishments?.includes(establishment.establishmentId) || formikProps?.values?.establishments?.includes(establishment.establishmentId)) || []}
                        onChange={(_, establishments) => {
                          setFieldValue('establishments', establishments?.map(establishment => establishment?.establishmentId));

                          if(formikProps) {
                            formikProps.setFieldValue('establishments', establishments?.map(establishment => establishment?.establishmentId));
                          }
                        }}
                        getOptionLabel={option => option.label}
                        getOptionSelected={(option, value) => option.establishmentId == value.establishmentId}
                        renderInput={params => (
                          <TextField
                            {...params}
                            variant="outlined"
                            color="primary"
                            label="Vincular a estabelecimentos"
                            error={errors?.establishments || formikProps?.errors?.establishments}
                            helperText={errors?.establishments || formikProps?.errors?.establishments}
                          />
                        )}
                        renderOption={(option, { selected }) => (
                          <li color="primary" className={classes.establishmentsRenderOption}>
                            <Checkbox checked={selected} />
                            <Typography color="primary">{option.label}</Typography>
                          </li>
                        )}
                        renderTags={(options, getTagProps) => {
                          return options.map((option, index) => (
                            <Chip
                              {...getTagProps({ index })}
                              style={{ backgroundColor: getEstablishmentColor(option?.establishmentId) }}
                              label={option.label}
                              onClick={() => {
                                if(isUserAdmin) {
                                  dispatch(setEstablishmentId({ establishmentId: option?.establishmentId }));
                                  history.push('/estabelecimento');
                                }
                              }}
                            />
                          ))
                        }}
                      />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <EmployeesDescription profileId={profileId || formikProps?.values?.profileId} />
                  </Grid>
                  {!(+employeeId == +userId) && (
                    <Loader isLoading={isRestrictionsLoading}>
                      <Grid container>
                        {(!!profileId || !!formikProps?.values?.profileId) && profilesToDisplay?.includes(+profileId || +formikProps?.values?.profileId) ? (
                          <>
                            {(+profileId == 2 || +formikProps?.values?.profileId == 2) ? (
                              <>
                                <Grid item xs={12} className={classes.grid}>
                                  <Typography variant="h6" color="primary">
                                    Restrições personalizadas do usuário {getProfileTitle(profileId || formikProps?.values?.profileId)}
                                  </Typography>
                                </Grid>
                                <Grid container>
                                  {restrictionsTypes
                                  ?.filter(restriction => siteRestrictionsId?.includes(+restriction?.id))
                                  ?.map(restriction => (
                                    <Grid item xs={12} className={classes.grid} style={{ marginTop: 15 }}>
                                      <Typography variant="h6" color="textSecondary">
                                        {restriction?.label}
                                      </Typography>
                                      <Grid container>
                                        {restrictionsUser
                                        ?.filter(userRestriction => +userRestriction?.type == +restriction?.id)
                                        ?.map(userRestriction => (
                                          <Grid item md={6} xs={12}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  name="userRestrictions"
                                                  color="primary"
                                                  id={`userRestrictions.${userRestriction?.restrictionId}`}
                                                  checked={selectedUserRestrictionsId?.includes(+userRestriction?.restrictionId)}
                                                  value={userRestriction?.restrictionId}
                                                  onChange={event => {
                                                    setSelectedUserRestrictionsId(selectRestriction(selectedUserRestrictionsId, event.target.value));

                                                    if(formikProps) {
                                                      formikProps.setFieldValue('userRestrictions', selectRestriction(selectedUserRestrictionsId, event.target.value));
                                                    }
                                                  }}
                                                />
                                              }
                                              label={
                                                <>
                                                  {getEmployeeSiteTooltip(userRestriction?.restrictionId) ? (
                                                    <Tooltip
                                                      placement="top"
                                                      title={
                                                        <Typography>{getEmployeeSiteTooltip(userRestriction?.restrictionId)}</Typography>
                                                      }
                                                      enterTouchDelay={0}
                                                    >
                                                      <Typography color="primary">
                                                        {userRestriction?.name}
                                                      </Typography>
                                                    </Tooltip>
                                                  ) : (
                                                    <Typography color="primary">
                                                      {userRestriction?.name}
                                                    </Typography>
                                                  )}
                                                </>
                                              }
                                            />
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </Grid>
                                  ))}
                                </Grid>
                              </>
                            ) : (
                              <>
                                <Grid item xs={12} className={classes.grid}>
                                  <Typography variant="h6" color="primary">
                                    Restrições personalizadas do usuário {getProfileTitle(profileId || formikProps?.values?.profileId)}
                                  </Typography>
                                </Grid>
                                <Grid container>
                                  {restrictionsTypes
                                  ?.filter(restriction => appRestrictionsId?.includes(restriction?.id))
                                  ?.map(restriction => (
                                    <Grid item xs={12} className={classes.grid} style={{ marginTop: 15 }}>
                                      <Typography variant="h6" color="textSecondary">
                                        {restriction?.label}
                                      </Typography>
                                      <Grid container>
                                        {getRestrictionsApp(establishments || formikProps.values.establishments, profileId || formikProps.values.profileId)
                                        ?.filter(appRestriction => +appRestriction?.type == restriction?.id)
                                        ?.map(appRestriction => (
                                          <Grid item md={6} xs={12}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  name="appRestrictions"
                                                  color="primary"
                                                  id={`appRestriction.${appRestriction?.id}`}
                                                  checked={selectedAppRestrictionsId?.includes(+appRestriction?.id)}
                                                  value={appRestriction?.id}
                                                  onChange={event => {
                                                    setSelectedAppRestrictionsId(selectRestriction(selectedAppRestrictionsId, event.target.value));

                                                    if(formikProps) {
                                                      formikProps.setFieldValue('appRestrictions', selectRestriction(selectedAppRestrictionsId, event.target.value));
                                                    }
                                                  }}
                                                />
                                              }
                                              label={
                                                <>
                                                  {getEmployeeAppTooltip(appRestriction?.id) ? (
                                                    <Tooltip
                                                      placement="top"
                                                      title={
                                                        <Typography>{getEmployeeAppTooltip(appRestriction?.id)}</Typography>
                                                      }
                                                      enterTouchDelay={0}
                                                    >
                                                      <Typography color="primary">
                                                        {appRestriction?.description}
                                                      </Typography>
                                                    </Tooltip>
                                                  ) : (
                                                    <Typography color="primary">
                                                      {appRestriction?.description}
                                                    </Typography>
                                                  )}
                                                </>
                                              }
                                            />
                                          </Grid>
                                        ))}
                                      </Grid>
                                    </Grid>
                                  ))}
                                </Grid>
                              </>
                            )}
                          </>
                        ) : null}
                      </Grid>
                    </Loader>
                  )}
                  {(isEmployeeRegister || (isInitialRegistration && !employeeId)) && (
                    <>
                      <Grid container>
                        <Grid item md={6} xs={12}>
                          <Grid container>
                            <Grid item xs={12} className={classes.grid} style={{ marginTop: 10 }}>
                              {getPasswordStrength(password || formikProps?.values?.password || '')}
                            </Grid>
                            <Grid item xs={12} className={classes.grid} style={{ marginTop: 10, marginBottom: 10 }}>
                              {getPasswordRules(password || formikProps?.values?.password || '')}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid container>
                        <Grid item md={6} xs={12} className={classes.grid}>
                          <Input
                            name="password"
                            type="password"
                            label="Senha"
                            placeholder="Senha"
                            value={password || formikProps?.values?.password}
                            error={errors?.password || formikProps?.errors?.password}
                            helperText={errors?.password || formikProps?.errors?.password}
                            onChange={e => {
                              handleChange(e);

                              if(formikProps) {
                                formikProps.handleChange(e);
                              }
                            }}
                          />
                        </Grid>
                        <Grid item md={6} xs={12} className={classes.grid}>
                          <Input
                            name="passwordConfirm"
                            type="password"
                            label="Confirmar senha"
                            placeholder="Confirmar senha"
                            value={passwordConfirm || formikProps?.values?.passwordConfirm}
                            error={errors?.passwordConfirm || formikProps?.errors?.passwordConfirm}
                            helperText={errors?.passwordConfirm || formikProps?.errors?.passwordConfirm}
                            onChange={e => {
                              handleChange(e);

                              if(formikProps) {
                                formikProps.handleChange(e);
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <CardFooter>
                      <div style={{ display: 'flex', flexDirection: isSm ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        {(isUserAdmin && +mainUser == 1) && (
                          <Button
                            fullWidth={isSm}
                            type="button"
                            color="primary"
                            loading={isMainUserLoading}
                            onClick={() => dispatch(setMainUserSL(userId, employeeId))}
                          >
                            Vincular Cliente SL
                          </Button>
                        )}
                        <div style={{ display: 'flex', flexDirection: isSm ? 'column' : 'row', justifyContent: 'flex-end', gap: 10, width: isSm ? '100%' : 'auto', marginTop: isSm ? 10 : 0 }}>
                          {(isUserAdmin && isEmployeeEdit && +submit?.profileId == 2) && (
                            <Button
                              fullWidth={isSm}
                              type="button"
                              color="primary"
                              loading={isLoading}
                              onClick={() => setIsMainUserModalOpen(true)}
                            >
                              {(+mainUser == 1) ? 'Desativar usuário principal' : 'Ativar usuário principal'}
                            </Button>
                          )}
                          {isEmployeeEdit && (
                            <Button
                              fullWidth={isSm}
                              type="button"
                              color="primary"
                              loading={isLoading}
                              onClick={() => setIsPasswordUpdateOpen(true)}
                            >
                              Alterar Senha
                            </Button>
                          )}
                          <Button
                            fullWidth={isSm}
                            type="submit"
                            color="success"
                            loading={isLoading}
                            onClick={event => {
                              if(isInitialRegistration) {
                                event.preventDefault();

                                if(formikProps) {
                                  formikProps.submitForm();
                                }
                              }
                            }}
                          >
                            Salvar
                          </Button>
                        </div>
                      </div>
                    </CardFooter>
                  </Grid>
                </Loader>
              </Form>
            </Grid>
          );
        }}
      </Formik>
    </Card>
  );
}